import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';



const ImportedQRCard = ({ 
  qrData, 
  onUpdate, 
  onRemove, 
  campaigns = [], 
  isSelected = false, 
  onSelect 
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: qrData?.name || '',
    content: qrData?.content || '',
    detectedType: qrData?.detectedType || 'text',
    campaign: qrData?.campaign || null,
    originalUrl: qrData?.originalUrl || qrData?.content || '',
    trackableUrl: qrData?.trackableUrl || ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [urlGeneration, setUrlGeneration] = useState({
    loading: false,
    error: null
  });

  const qrTypes = [
    { value: 'url', label: 'Website URL', icon: 'Link' },
    { value: 'text', label: 'Plain Text', icon: 'FileText' },
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'phone', label: 'Phone', icon: 'Phone' },
    { value: 'sms', label: 'SMS', icon: 'MessageSquare' },
    { value: 'wifi', label: 'Wi-Fi', icon: 'Wifi' },
    { value: 'vcard', label: 'Contact Card', icon: 'User' },
    { value: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' }
  ];

  const getStatusDisplay = () => {
    switch (qrData?.status) {
      case 'ready':
        return {
          icon: 'CheckCircle',
          text: 'Ready to Save',
          className: 'text-green-600 bg-green-50',
          iconClassName: 'text-green-600'
        };
      case 'saved':
        return {
          icon: 'Database',
          text: 'Saved to Database',
          className: 'text-blue-600 bg-blue-50',
          iconClassName: 'text-blue-600'
        };
      case 'error':
        return {
          icon: 'XCircle',
          text: 'Decoding Failed',
          className: 'text-red-600 bg-red-50',
          iconClassName: 'text-red-600'
        };
      case 'skipped':
        return {
          icon: 'AlertCircle',
          text: 'Skipped (Duplicate)',
          className: 'text-yellow-600 bg-yellow-50',
          iconClassName: 'text-yellow-600'
        };
      default:
        return {
          icon: 'Clock',
          text: 'Processing',
          className: 'text-gray-600 bg-gray-50',
          iconClassName: 'text-gray-600'
        };
    }
  };

  const handleRetryDecoding = async () => {
    if (!qrData?.originalFile) return;
    
    setIsProcessing(true);
    try {
      // Re-import the QR image processing service
      const { QRImageProcessingService } = await import('../../../services/qrImageProcessingService');
      
      // Validate image first
      const validation = await QRImageProcessingService?.validateAndNormalizeImage(qrData?.originalFile);
      if (!validation?.isValid) {
        onUpdate(qrData?.id, { 
          status: 'error', 
          error: validation?.error 
        });
        return;
      }

      // Create image and canvas for processing
      const img = new Image();
      const imageUrl = URL.createObjectURL(qrData?.originalFile);
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error('Failed to load image'));
        img.crossOrigin = 'anonymous';
        img.src = imageUrl;
      });

      const canvas = document.createElement('canvas');
      const ctx = canvas?.getContext('2d');
      
      const maxSize = 2048;
      let { width, height } = img;
      
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      // Try enhanced decoding
      const result = await QRImageProcessingService?.decodeQRFromImage(qrData?.originalFile, img, canvas);
      
      if (result?.content) {
        const styling = await QRImageProcessingService?.extractQRStyling(canvas);
        const detectedType = QRImageProcessingService?.detectQRType(result?.content);
        
        onUpdate(qrData?.id, {
          content: result?.content,
          detectedType,
          styling,
          status: 'ready',
          error: null,
          metadata: {
            ...qrData?.metadata,
            retryAttempted: true,
            decodingMethod: result?.method,
            processedDimensions: { width, height }
          }
        });
      } else {
        onUpdate(qrData?.id, {
          status: 'error',
          error: 'Still could not decode QR code after enhanced processing. The image may not contain a valid QR code or the code may be too damaged to read.',
          metadata: {
            ...qrData?.metadata,
            retryAttempted: true,
            enhancedProcessingFailed: true
          }
        });
      }

      URL.revokeObjectURL(imageUrl);
    } catch (error) {
      onUpdate(qrData?.id, {
        status: 'error',
        error: `Retry failed: ${error?.message}`,
        metadata: {
          ...qrData?.metadata,
          retryAttempted: true,
          retryError: error?.message
        }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Fixed: Generate trackable URL when QR is ready - simplified approach
  const generateTrackableUrl = async () => {
    if (!qrData?.content) return;
    
    setUrlGeneration({ loading: true, error: null });
    
    try {
      // Instead of creating temporary QR, just generate a preview URL
      // The actual save will happen through the bulk save process
      const shortCode = Math.random()?.toString(36)?.substring(2, 10);
      const previewTrackingUrl = `${window.location?.origin}/qr/${shortCode}`;
      
      setEditData(prev => ({ 
        ...prev, 
        trackableUrl: previewTrackingUrl 
      }));
      
      // Update the QR data with preview trackable URL (not saved to DB yet)
      onUpdate(qrData?.id, { 
        trackableUrl: previewTrackingUrl,
        previewShortCode: shortCode,
        status: 'ready' // Keep as ready for bulk save
      });
      
    } catch (error) {
      setUrlGeneration({ loading: false, error: error?.message || 'Failed to generate trackable URL' });
    } finally {
      setUrlGeneration({ loading: false, error: null });
    }
  };

  // Handle URL field updates
  const handleUrlUpdate = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    
    // If original URL is updated and it's a URL type, update content
    if (field === 'originalUrl' && editData?.detectedType === 'url') {
      setEditData(prev => ({ ...prev, content: value }));
    }
  };

  const handleSaveEdit = () => {
    onUpdate(qrData?.id, {
      name: editData?.name,
      content: editData?.content,
      detectedType: editData?.detectedType,
      campaign: editData?.campaign,
      originalUrl: editData?.originalUrl,
      trackableUrl: editData?.trackableUrl
    });
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditData({
      name: qrData?.name || '',
      content: qrData?.content || '',
      detectedType: qrData?.detectedType || 'text',
      campaign: qrData?.campaign || null,
      originalUrl: qrData?.originalUrl || qrData?.content || '',
      trackableUrl: qrData?.trackableUrl || ''
    });
    setEditMode(false);
  };

  const getTypeIcon = (type) => {
    const typeConfig = qrTypes?.find(t => t?.value === type);
    return typeConfig?.icon || 'QrCode';
  };

  return (
    <div className={`bg-white rounded-lg border-2 transition-all duration-200 ${
      isSelected ? 'border-blue-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <div className="p-4">
        {/* Header with selection and status */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start space-x-3">
            {/* Selection checkbox */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelect(e?.target?.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                disabled={qrData?.status === 'error'}
              />
            </label>
            
            {/* File info */}
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-medium text-gray-900 truncate">
                {qrData?.fileName}
              </h3>
              <div className="flex items-center text-xs text-gray-500 mt-1 space-x-2">
                <span>{(qrData?.metadata?.fileSize / 1024)?.toFixed(1)}KB</span>
                <span>•</span>
                <span>{qrData?.metadata?.fileType}</span>
                {qrData?.metadata?.originalDimensions && (
                  <>
                    <span>•</span>
                    <span>{qrData?.metadata?.originalDimensions?.width}×{qrData?.metadata?.originalDimensions?.height}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Status badge */}
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
            getStatusDisplay()?.className
          }`}>
            <Icon name={getStatusDisplay()?.icon} size={12} className={getStatusDisplay()?.iconClassName} />
            <span>{getStatusDisplay()?.text}</span>
          </div>
        </div>

        {/* Content based on status */}
        {qrData?.status === 'error' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-medium text-red-800 mb-1">Decoding Failed</h4>
                <p className="text-xs text-red-700 leading-relaxed">{qrData?.error}</p>
                
                {/* Enhanced retry button */}
                <div className="mt-3 flex items-center space-x-2">
                  <Button
                    onClick={handleRetryDecoding}
                    disabled={isProcessing}
                    size="sm"
                    variant="outline"
                    className="text-red-700 border-red-300 hover:bg-red-50"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b border-red-600 mr-1"></div>
                        <span>Retrying...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="RefreshCw" size={12} className="mr-1" />
                        <span>Retry with Enhanced Processing</span>
                      </>
                    )}
                  </Button>
                  
                  {qrData?.metadata?.retryAttempted && (
                    <span className="text-xs text-red-600">
                      Enhanced processing attempted
                    </span>
                  )}
                </div>

                {/* Troubleshooting tips for failed decoding */}
                <div className="mt-3 p-2 bg-red-25 rounded border border-red-100">
                  <h5 className="text-xs font-medium text-red-800 mb-1">Troubleshooting Tips:</h5>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    <li>• Ensure QR code fills most of the image frame</li>
                    <li>• Try a higher resolution or less compressed image</li>
                    <li>• Make sure the QR code has good contrast (dark on light background)</li>
                    <li>• Avoid rotated, skewed, or partially obscured QR codes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : qrData?.content ? (
          <div className="space-y-3">
            {/* QR Content Display */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">Detected Content</h4>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  {qrData?.detectedType || 'text'}
                </span>
              </div>
              <div className="bg-white rounded border p-2 max-h-20 overflow-y-auto">
                <code className="text-xs text-gray-800 break-all">{qrData?.content}</code>
              </div>
              
              {/* Enhanced metadata display */}
              {qrData?.metadata?.decodingMethod && (
                <div className="mt-2 text-xs text-gray-500">
                  Decoded using: {qrData?.metadata?.decodingMethod}
                </div>
              )}
            </div>

            {/* Enhanced Editable fields with URL options */}
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    QR Name *
                  </label>
                  <Input
                    value={editData?.name}
                    onChange={(e) => setEditData(prev => ({ ...prev, name: e?.target?.value }))}
                    placeholder="Enter QR code name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    QR Type
                  </label>
                  <Select
                    value={editData?.detectedType}
                    onChange={(value) => setEditData(prev => ({ ...prev, detectedType: value }))}
                    options={qrTypes?.map(type => ({ value: type?.value, label: type?.label }))}
                  />
                </div>
              </div>
              
              {/* Original URL Field - Show for URL types */}
              {(editData?.detectedType === 'url' || editData?.detectedType === 'whatsapp') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center space-x-2">
                    <Icon name="Link" size={16} className="text-blue-600" />
                    <span>Original URL *</span>
                  </label>
                  <div className="relative">
                    <Input
                      value={editData?.originalUrl}
                      onChange={(e) => handleUrlUpdate('originalUrl', e?.target?.value)}
                      placeholder="https://example.com"
                      className="pr-10"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <Icon 
                        name={editData?.originalUrl?.startsWith('http') ? 'CheckCircle' : 'AlertCircle'} 
                        size={16} 
                        className={editData?.originalUrl?.startsWith('http') ? 'text-green-500' : 'text-yellow-500'} 
                      />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    This is the destination URL where users will be redirected
                  </p>
                </div>
              )}
              
              {/* Trackable URL Field - Show for all ready QR codes */}
              {qrData?.status === 'ready' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="BarChart" size={16} className="text-purple-600" />
                      <span>Trackable URL</span>
                    </div>
                    <Button
                      onClick={generateTrackableUrl}
                      size="sm"
                      variant="outline"
                      disabled={urlGeneration?.loading}
                      className="text-xs px-2 py-1"
                    >
                      {urlGeneration?.loading ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-purple-600 mr-1"></div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Icon name="RefreshCw" size={12} className="mr-1" />
                          <span>Generate</span>
                        </>
                      )}
                    </Button>
                  </label>
                  <div className="relative">
                    <Input
                      value={editData?.trackableUrl}
                      onChange={(e) => handleUrlUpdate('trackableUrl', e?.target?.value)}
                      placeholder="Will be generated automatically"
                      className="pr-20 font-mono text-sm"
                      readOnly={!editData?.trackableUrl}
                    />
                    {editData?.trackableUrl && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
                        <button
                          onClick={() => navigator.clipboard?.writeText(editData?.trackableUrl)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Copy to clipboard"
                        >
                          <Icon name="Copy" size={14} className="text-gray-500 hover:text-gray-700" />
                        </button>
                        <Icon name="ExternalLink" size={14} className="text-blue-500" />
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Short URL for tracking scans and analytics
                  </p>
                  {urlGeneration?.error && (
                    <p className="mt-1 text-xs text-red-600">{urlGeneration?.error}</p>
                  )}
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content
                </label>
                <textarea
                  value={editData?.content}
                  onChange={(e) => setEditData(prev => ({ ...prev, content: e?.target?.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {campaigns?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign
                  </label>
                  <Select
                    value={editData?.campaign?.id || ''}
                    onChange={(value) => {
                      const campaign = campaigns?.find(c => c?.id === value);
                      setEditData(prev => ({ ...prev, campaign }));
                    }}
                    options={[
                      { value: '', label: 'No Campaign' },
                      ...campaigns?.map(campaign => ({ 
                        value: campaign?.id, 
                        label: campaign?.name 
                      }))
                    ]}
                  />
                </div>
              )}

              {/* URL Summary Card - Show when both URLs are available */}
              {(editData?.originalUrl || editData?.trackableUrl) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                    <Icon name="Info" size={16} className="mr-2" />
                    URL Configuration
                  </h4>
                  <div className="space-y-2 text-sm">
                    {editData?.originalUrl && (
                      <div>
                        <span className="font-medium text-blue-800">Original:</span>
                        <div className="font-mono text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded mt-1 break-all">
                          {editData?.originalUrl}
                        </div>
                      </div>
                    )}
                    {editData?.trackableUrl && (
                      <div>
                        <span className="font-medium text-blue-800">Trackable:</span>
                        <div className="font-mono text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded mt-1 break-all">
                          {editData?.trackableUrl}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  size="sm"
                  disabled={!editData?.name || (!editData?.originalUrl && editData?.detectedType === 'url')}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <div className="text-center">
              <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
              <p className="text-sm">Processing QR code...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportedQRCard;